import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import TableLayout from '../../../components/TableLayout';
import { orderService } from '../../../data';

const OrdersContainer = styled.div`
  padding: 20px;
  background-color: #F9F6F2;
  height: calc(100vh - 200px);
  display: grid;
  grid-template-columns: 1fr 800px;
  gap: 20px;
  overflow: hidden;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 600px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr 400px;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: calc(100vh - 200px);
    gap: 15px;
  }
`;

const Title = styled.h1`
  color: #4A3C31;
  margin-bottom: 20px;
  font-size: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 15px;
  }
`;

const OrderListSection = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;

  @media (max-width: 768px) {
    height: auto;
    min-height: 400px;
  }
`;

const OrdersTableContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 0; /* Important for flex child scrolling */

  @media (max-width: 768px) {
    min-height: 350px;
  }
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
  min-height: 0; /* Important for flex child scrolling */

  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const OrderDetailsSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  height: 100%;

  @media (max-width: 768px) {
    height: auto;
    min-height: 300px;
    padding: 15px;
  }
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const DetailLabel = styled.div`
  width: 120px;
  color: #666;
  font-weight: 500;

  @media (max-width: 768px) {
    width: auto;
    font-size: 0.9rem;
  }
`;

const DetailValue = styled.div`
  flex: 1;
  color: #2C1810;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-top: 15px;
  }
`;

const Th = styled.th`
  background: #F5EDE3;
  padding: 12px;
  text-align: left;
  color: #4A3C31;
  font-weight: 600;

  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 0.8rem;
  }
`;

const Td = styled.td`
  padding: 12px;
  border-top: 1px solid #eee;
  color: #2C1810;

  @media (max-width: 768px) {
    padding: 8px 6px;
    font-size: 0.8rem;
  }
`;

const OrdersTab = ({ customerId, market, orders, loading: externalLoading, error }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [sortField, setSortField] = useState('order_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [cachedPages, setCachedPages] = useState(new Map());
  const [loadingPages, setLoadingPages] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const pageSize = 10;

  const loadingDetailsRef = React.useRef(false);

  // Load a specific page of orders
  const loadOrders = useCallback(async (page) => {
    // If already cached, use cached data
    if (cachedPages.has(page)) {
      setCurrentOrders(cachedPages.get(page));
      return;
    }

    // If already loading this page, don't duplicate the request
    if (loadingPages.has(page)) {
      return;
    }

    try {
      setLoading(true);
      setLoadingPages(prev => new Set(prev).add(page));
      const response = await orderService.getOrders(customerId, market, page, pageSize);
      
      // Cache the page data
      setCachedPages(prev => new Map(prev).set(page, response.items));
      setTotalPages(response.total_pages);
      
      // If this is the current page, update the display
      if (page === currentPage) {
        setCurrentOrders(response.items);
      }
    } catch (err) {
      console.error('Failed to load orders for page:', page, err);
    } finally {
      setLoading(false);
      setLoadingPages(prev => {
        const newSet = new Set(prev);
        newSet.delete(page);
        return newSet;
      });
    }
  }, [customerId, market, cachedPages, loadingPages, currentPage]);

  // Load current page immediately
  useEffect(() => {
    loadOrders(currentPage);
  }, [currentPage, loadOrders]);

  // Background loading of adjacent pages
  useEffect(() => {
    const preloadAdjacentPages = async () => {
      const pagesToPreload = [];
      
      // Preload next page if it exists
      if (currentPage < totalPages) {
        pagesToPreload.push(currentPage + 1);
      }
      
      // Preload previous page if it exists
      if (currentPage > 1) {
        pagesToPreload.push(currentPage - 1);
      }

      // Load pages in background
      for (const page of pagesToPreload) {
        if (!cachedPages.has(page) && !loadingPages.has(page)) {
          // Use setTimeout to make this truly background
          setTimeout(() => loadOrders(page), 100);
        }
      }
    };

    if (totalPages > 1) {
      preloadAdjacentPages();
    }
  }, [currentPage, totalPages, cachedPages, loadingPages, loadOrders]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    
    // If the page is already cached, show it immediately
    if (cachedPages.has(newPage)) {
      setCurrentOrders(cachedPages.get(newPage));
    }
  }, [cachedPages]);

  const loadOrderDetails = useCallback(async (orderNumber) => {
    if (loadingDetailsRef.current) return;
    loadingDetailsRef.current = true;
    
    setLoadingDetails(true);
    try {
      const details = await orderService.getOrderDetails(orderNumber, market, customerId);
      setSelectedOrder(details);
    } catch (err) {
      console.error('Failed to load order details:', err);
    }
    setLoadingDetails(false);
    loadingDetailsRef.current = false;
  }, [market, customerId]);

  const handleRowClick = useCallback((row) => {
    loadOrderDetails(row.order_number);
  }, [loadOrderDetails]);

  const handleSort = useCallback((field) => {
    setSortDirection(current => 
      sortField === field 
        ? current === 'asc' ? 'desc' : 'asc'
        : 'desc'
    );
    setSortField(field);
  }, [sortField]);

  // Clear cache when customerId or market changes
  useEffect(() => {
    setCachedPages(new Map());
    setLoadingPages(new Set());
    setCurrentPage(1);
  }, [customerId, market]);

  return (
    <OrdersContainer>
      <OrderListSection>
        <Title>Orders</Title>
        {error && <div style={{ color: 'red', margin: '10px 0' }}>{error}</div>}
        <OrdersTableContainer>
          <TableWrapper>
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#4A3C31', fontWeight: 500 }}>
                Loading orders...
              </div>
            ) : (
              <TableLayout 
                columns={[ 
                  {  
                    field: 'order_number',
                    label: 'Invoice#',
                    sortable: true
                  },
                  {  
                    field: 'order_date',
                    label: 'Date',
                    sortable: true,
                    render: (row) => new Date(row.order_date).toLocaleDateString()
                  },
                  {  
                    field: 'status_description',
                    label: 'Status',
                    sortable: true
                  }
                ]}
                data={currentOrders}
                loading={loading}
                onRowClick={handleRowClick}
                selectedRow={selectedOrder?.[0]?.order_number}
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                pageSize={pageSize}
              />
            )}
          </TableWrapper>
        </OrdersTableContainer>
      </OrderListSection>

      <OrderDetailsSection>
        {selectedOrder ? (
          loadingDetails ? (
            <div>Loading details...</div>
          ) : (
            <>
              <Title>Order Details</Title>
              <DetailRow>
                <DetailLabel>Ref Invoice #</DetailLabel>
                <DetailValue>{selectedOrder[0]?.order_number}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>Date</DetailLabel>
                <DetailValue>
                  {selectedOrder[0]?.order_date && new Date(selectedOrder[0].order_date).toLocaleDateString()}
                </DetailValue>
              </DetailRow>                         
              
              <Table>
                <thead>
                  <tr>                    
                    <Th>Product</Th>
                    <Th>Ordered</Th>
                    <Th>Delivered</Th>
                    <Th>Price</Th>
                    <Th>Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.map((detail, index) => (
                    <tr key={`${detail.order_number}-${detail.sku}-${index}`}>                        
                      <Td>{detail.description}</Td>
                      <Td>{detail.qty_ordered}</Td>
                      <Td>{detail.qty_delivered}</Td>
                      <Td>${parseFloat(detail.unit_price).toFixed(2)}</Td>
                      <Td>${(detail.qty_delivered * parseFloat(detail.unit_price)).toFixed(2)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>              
            </>
          )
        ) : (
          <div>Select an order to view details</div>
        )}
      </OrderDetailsSection>
    </OrdersContainer>
  );
};

export default OrdersTab;
